import React, { useCallback, useEffect, useState } from "react";
import BreadcrumbComponent from "../common/BreadcrumbComponent";
import FormComponent from "../common/FormComponent";
import Header from "../common/Header";
import { useAppDispatch } from "../../hooks/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import ErrorMessageAlert, { useAlert } from "../common/Alert";
import {
  updateElementValuetemplateReducer,
  useTemplateReducer,
  createUpdateTemplate,
  resetTemplateData,
  getTemplateDataById,
} from "../../redux/slice/templateSlice";
import { useCountyListReducer } from "../../redux/slice/countyListSlice";
import {
  ROLE_COUNTY_ADMIN,
  SEARCH_WAR_TEMPLATE_TYPE_ORDER,
  FIELDS_APPLICATION_ORDER,
} from "../../constants/common";
import { useLoginReducer } from "../../redux/slice/loginSlice";

let defaultCountySchema = yup.object().shape({
  templateApplicationCountyId: yup.number().min(1, "County is required."),
  templateName: yup.string().required("Template Name is required."),
  applicationTitle: yup.string().required("Application Title is required."),
  applicationInfo: yup.string().required("Application Info is required."),
  orderTitle: yup.string().required("Order Title is required."),
  orderInfo: yup.string().required("Order Info is required."),
});

interface ApplicationAndOrderTemProps {}

const ApplicationAndOrderTemp: React.FC<ApplicationAndOrderTemProps> = (
  props
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const templateReducer = useTemplateReducer();
  const [errorKeys, setErrorKeys] = useState([""]);
  const errorMessageAlert = useAlert();
  const { countyList } = useCountyListReducer();
  const location = useLocation();
  const { userType } = useLoginReducer();

  const handleSubmit = async () => {
    try {
      await defaultCountySchema.validate(templateReducer, {
        abortEarly: false,
      });

      let templateObj = {
        templateId: 0,
        countyIdParam: templateReducer.templateApplicationCountyId,
        templateName: templateReducer.templateName,
        warrantFormName: SEARCH_WAR_TEMPLATE_TYPE_ORDER,
        templateField: FIELDS_APPLICATION_ORDER,
        templateValue: [
          templateReducer.applicationTitle,
          templateReducer.applicationInfo,
          templateReducer.orderTitle,
          templateReducer.orderInfo,
        ],
      };

      let fetchType;
      if (location.pathname.split("/")[1].includes("edit")) {
        fetchType = "PUT";
        templateObj.templateId = templateReducer.templateId;
      } else {
        fetchType = "POST";
      }
      dispatch(createUpdateTemplate({ templateObj, fetchType, navigate }));
    } catch (err: any) {
      handleError(
        err.inner.map((e: any) => e.path),
        err.errors
      );
    }
  };

  const handleInputChange = (e: any) => {
    dispatch(
      updateElementValuetemplateReducer({
        elementName: e.target.name,
        value: e.target.value,
      })
    );
  };

  const handleError = useCallback(
    (_errorKeys: string[], errorMessages: string[]) => {
      setErrorKeys(_errorKeys);
      errorMessageAlert.handleError(errorMessages);
      window.scroll({ top: 0, behavior: "smooth" });
    },
    []
  );

  useEffect(() => {
    dispatch(resetTemplateData());
    dispatch(
      updateElementValuetemplateReducer({
        elementName: "templateType",
        value: SEARCH_WAR_TEMPLATE_TYPE_ORDER,
      })
    );
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetTemplateData());
    };
  }, []);

  useEffect(() => {
    const templateId = location.pathname.split("/")[2];

    if (templateId) {
      dispatch(resetTemplateData());
      dispatch(getTemplateDataById(templateId));
    }
  }, [dispatch, location, navigate]);

  useEffect(() => {
    if (templateReducer.templateError) {
      handleError([], [templateReducer.templateError]);
      dispatch(
        updateElementValuetemplateReducer({
          elementName: "templateError",
          value: "",
        })
      );
    }
  }, [templateReducer.templateError, dispatch, handleError]);

  const handleSelectChange = (name: string) => (e: any) => {
    dispatch(
      updateElementValuetemplateReducer({
        elementName: name,
        value: e.value,
      })
    );
  };

  const handleCkEditorValChange = (name: any, editor: any) => {
    const data = editor.getData();
    dispatch(
      updateElementValuetemplateReducer({
        elementName: name,
        value: data,
      })
    );
  };

  let itemLinkList = [
    { itemLink: "/", itemName: "DashBoard" },
    { itemLink: "/TemplateList", itemName: "Template List" },
  ];

  const data = [
    {
      type: "selectInput",
      props: {
        options: [
          { label: "-- SELECT --", value: 0, Judicial: "" },
          ...countyList.map((county: any) => {
            return {
              label: county.countyName,
              value: county.countyID,
              Judicial: county.judicialNumberText,
            };
          }),
        ],
        value: countyList
          .map((county: any) => {
            return {
              label: county.countyName,
              value: county.countyID,
              Judicial: county.judicialNumberText,
            };
          })
          .find(
            (item) =>
              item.value ===
              (location.pathname.split("/")[2]
                ? templateReducer.templateApplicationCountyId
                : 0)
          ),
        onChange: handleSelectChange("templateApplicationCountyId"),
        placeholder: "Select County",
        name: "templateApplicationCountyId",
        defaultValue: { label: "-- SELECT --", value: 0, Judicial: "" },
        isError: errorKeys.includes("templateApplicationCountyId"),
        isDisabled:
          location.pathname.split("/")[1].includes("edit") ||
          (location.pathname.split("/")[1].includes("copy") &&
            userType === ROLE_COUNTY_ADMIN),
      },
      colSize: 4,
      label: "County",
      inLine: true,
    },
    {
      type: "textInput",
      props: {
        type: "text",
        placeholder: "Enter Template Name",
        onChange: handleInputChange,
        name: "templateName",
        value: templateReducer.templateName,
        isError: errorKeys.includes("templateName"),
        disabled: location.pathname.split("/")[1].includes("edit") || false,
      },
      colSize: 4,
      label: "Template Name",
      inLine: true,
    },
    {
      type: "textInput",
      props: {
        type: "text",
        placeholder: "Enter Template type",
        onChange: handleInputChange,
        name: "templateType",
        disabled: true,
        value: templateReducer.templateType,
      },
      colSize: 4,
      label: "Template type",
      inLine: true,
    },
    {
      type: "editor",
      props: {
        data: templateReducer.applicationTitle
          ? templateReducer.applicationTitle
          : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChange("applicationTitle", editor);
        },
        isError: errorKeys.includes("applicationTitle"),
      },
      colSize: 12,
      label: "Application Title",
    },
    {
      type: "editor",
      props: {
        data: templateReducer.applicationInfo
          ? templateReducer.applicationInfo
          : "",

        onChange: (event: any, editor: any) => {
          handleCkEditorValChange("applicationInfo", editor);
        },
        isError: errorKeys.includes("applicationInfo"),
      },
      label: "Application Information",
      colSize: 12,
    },
    {
      type: "editor",
      props: {
        data: templateReducer.orderTitle ? templateReducer.orderTitle : "",

        onChange: (event: any, editor: any) => {
          handleCkEditorValChange("orderTitle", editor);
        },
        isError: errorKeys.includes("orderTitle"),
      },
      colSize: 12,
      label: "Order Title",
    },
    {
      type: "editor",
      props: {
        data: templateReducer.orderInfo ? templateReducer.orderInfo : "",

        onChange: (event: any, editor: any) => {
          handleCkEditorValChange("orderInfo", editor);
        },
        isError: errorKeys.includes("orderInfo"),
      },
      colSize: 12,
      label: "Order Information",
    },
    {
      type: "button",
      props: {
        children: location.pathname.split("/")[1].includes("edit")
          ? "Update"
          : "Save",
        className: "btn-primary ms-2",
        onClick: handleSubmit,
      },
    },
    {
      type: "button",
      props: {
        children: "Cancel",
        className: "btn-danger ms-2",
        onClick: () => {
          navigate(-1);
        },
      },
    },
  ];

  return (
    <>
      {" "}
      <Header headerName="Application and Order" />
      <div className="main-body p-3">
        <BreadcrumbComponent
          itemList={itemLinkList}
          itemActive="Application and Order"
        />
        <ErrorMessageAlert
          messages={errorMessageAlert.messages}
          clearError={errorMessageAlert.clearError}
        />
        <FormComponent
          totalColumns={12}
          colSize={6}
          componentData={data}
          subHeader="Application and Order Form"
        />
      </div>
    </>
  );
};

export default ApplicationAndOrderTemp;
